
import React from "react";
import { Progress } from "./ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { cn } from "@/lib/utils";
import { CheckCircle2, Infinity } from "lucide-react";

interface QuotaDisplayProps {
  used: number;
  total: number;
  title: string;
  unit?: string;
  className?: string;
}

export const QuotaDisplay = ({ used, total, title, unit = "", className = "" }: QuotaDisplayProps) => {
  // ✅ Handle unlimited plans (total === -1)
  const isUnlimited = total === -1;
  
  if (isUnlimited) {
    return (
      <Card className={`shadow-sm border-green-200 ${className}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            {title}
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-green-600">
              <Infinity className="h-5 w-5" />
              <span className="font-semibold">Unlimited</span>
            </div>
            <div className="text-xs text-gray-500">
              {used} {unit} used • No limits
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const percentage = Math.min(100, Math.round((used / total) * 100));
  
  // Determine color based on usage percentage
  let progressColor = "bg-green-500";
  let borderColor = "border-gray-200";
  
  if (percentage >= 90) {
    progressColor = "bg-red-500";
    borderColor = "border-red-200";
  } else if (percentage >= 70) {
    progressColor = "bg-yellow-500";
    borderColor = "border-yellow-200";
  } else if (percentage >= 50) {
    borderColor = "border-blue-200";
  }
  
  return (
    <Card className={`shadow-sm ${borderColor} ${className}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Progress 
            value={percentage} 
            className={cn("h-2", progressColor)}
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>{used} {unit} used</span>
            <span>{Math.max(0, total - used)} {unit} remaining</span>
          </div>
          <div className="text-xs text-gray-400">
            {percentage}% of limit used
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
